import { useCallback, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import { useToast } from '@kanda-libs/ks-design-library';
import {
  FirebaseAuthService,
  actions,
  useCurrentUser,
  useMutate,
} from '@kanda-libs/ks-frontend-services';

import { useAppDispatch } from 'components/App';
import { URLS } from 'config';
import useApiError from 'hooks/useApiError';

import { cleanReturnUrl, extractIdFromUrl, getEmailFromUrl } from './helpers';

export default function useAuthLink() {
  const dispatch = useAppDispatch();
  const onError = useApiError('Error sending new email - please contact Kanda');

  const url = window.location.href;
  const urlRef = useRef<string | null>(null);

  const {
    user,
    isValidating: userIsValidating,
    revalidate,
    logout,
  } = useCurrentUser();

  const { mutate: parseActionCode } = useMutate(
    FirebaseAuthService.parseActionCode.bind(FirebaseAuthService),
  );

  const { mutate: signInWithEmailLink } = useMutate(
    FirebaseAuthService.signInWithEmailLink.bind(FirebaseAuthService),
  );

  const { showError } = useToast();

  const { push } = useHistory();

  const signIn = useCallback(
    (email, continueUrl) => {
      signInWithEmailLink(email, url).then(({ error: signInError }) => {
        if (signInError) {
          showError('Error signing in - new email sent');
          const id = extractIdFromUrl(continueUrl);

          dispatch(
            actions.resendJob({
              params: { id },
              onError,
              onSuccess: () => {
                push(URLS.expired);
              },
            }),
          );

          return;
        }
        revalidate();

        push(cleanReturnUrl(continueUrl));
      });
    },
    [dispatch, showError, signInWithEmailLink, url, revalidate, push, onError],
  );

  const emailSigninFlow = useCallback(
    async (data) => {
      const email = data?.continueUrl?.includes('?')
        ? getEmailFromUrl(data.continueUrl)
        : null;
      signIn(email, data?.continueUrl);
    },
    [signIn],
  );

  const logoutAndReload = useCallback(() => {
    logout().then(() => {
      window.location.reload();
    });
  }, [logout]);

  useEffect(() => {
    if (userIsValidating) return;
    if (url && url === urlRef.current) return;
    urlRef.current = url;
    if (user) {
      logoutAndReload();
      return;
    }
    parseActionCode(url).then(({ data, error }) => {
      if (error || !data) {
        // DEV_NOTE: handle code error here
        showError('error');
        return;
      }
      const { operation } = data || {};
      if (operation === 'EMAIL_SIGNIN') {
        emailSigninFlow(data);
        return;
      }
      // DEV_NOTE: Redirect to expired as only route should be email signin
      push(URLS.expired);
    });
  }, [
    url,
    parseActionCode,
    showError,
    push,
    emailSigninFlow,
    userIsValidating,
    user,
    logoutAndReload,
  ]);
}
